import { alpha, createTheme, responsiveFontSizes } from "@mui/material/styles";

const theme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: "#3b82f6", // Blue accent color (Speechify-style)
            light: "#60a5fa",
            dark: "#2563eb",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#8b5cf6", // Purple accent
            light: "#a78bfa",
            dark: "#7c3aed",
        },
        background: {
            default: "#0a0a0a", // Very dark background
            paper: "#1a1a1a", // Dark card/surface color
        },
        text: {
            primary: "#ffffff",
            secondary: "rgba(255, 255, 255, 0.7)",
            disabled: "rgba(255, 255, 255, 0.5)",
        },
        divider: "rgba(255, 255, 255, 0.1)",
    },
    typography: {
        fontSize: 14,
        h1: {
            fontSize: "4rem",
            fontWeight: 800,
        },
        h2: {
            fontSize: "3.5rem",
            fontWeight: 800,
        },
        h3: {
            fontSize: "2.75rem",
            fontWeight: 800,
        },
        h4: {
            fontSize: "2.25rem",
            fontWeight: 700,
        },
        h5: {
            fontSize: "2rem",
            fontWeight: 700,
        },
        h6: {
            fontSize: "1.75rem",
            fontWeight: 700,
        },
        subtitle1: {
            fontSize: "1.1rem",
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: "1rem",
            fontWeight: 500,
        },
        body1: {
            fontSize: "0.95rem",
        },
        body2: {
            fontSize: "0.875rem",
        },
        button: {
            fontSize: "0.875rem",
            fontWeight: 600,
        },
        caption: {
            fontSize: "0.75rem",
        },
    },
    shape: {
        borderRadius: 10,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    backgroundColor: "#0a0a0a",
                    backgroundAttachment: "fixed",
                    minHeight: "100vh",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 14,
                    borderColor: alpha(theme.palette.common.white, 0.1),
                    backgroundColor: theme.palette.background.paper,
                }),
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderColor: alpha(theme.palette.common.white, 0.1),
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: ({ theme }) => ({
                    borderColor: alpha(theme.palette.common.white, 0.2),
                }),
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.background.paper,
                    fontSize: "0.875rem", // Smaller input text
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(theme.palette.common.white, 0.3),
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                    },
                }),
                input: {
                    fontSize: "0.875rem", // Ensure input text is smaller
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: "0.875rem", // Smaller label text
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: "0.875rem", // Smaller select text
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: "0.875rem", // Smaller menu item text
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: "0.75rem", // Smaller helper text
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderBottomColor: alpha(theme.palette.common.white, 0.1),
                    paddingTop: 10,
                    paddingBottom: 10,
                }),
                head: ({ theme }) => ({
                    fontSize: "0.72rem",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: theme.palette.text.secondary,
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    borderRadius: "10px",
                    boxShadow: "none",
                    fontWeight: 700,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    borderRadius: "10px",
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    // Responsive typography is handled in typography variants above
                    // This ensures all Typography components respect the responsive sizing
                },
            },
        },
    },
});

// Apply responsive font sizes automatically
// This scales typography based on viewport size without manual breakpoints
// Options control the scaling factor and breakpoints
const responsiveTheme = responsiveFontSizes(theme, {
    factor: 1.5, // Reduced scale factor for more subtle scaling
    breakpoints: ["xs", "sm", "md"], // Apply at these breakpoints
    variants: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "subtitle1",
        "subtitle2",
        // Exclude body1, body2, button, caption from responsive scaling
        // to keep form elements at consistent sizes
    ],
});

export default responsiveTheme;
